import React, { useState, useEffect } from 'react';
import { Route, withRouter, Redirect, Switch } from 'react-router';
import { Layout } from './components/Layout';
import Login from './components/Auth/Login';
import Providers from './components/Maintenance/Providers';
import Globals from '../src/config/globals';
import PatientsHome from './components/Patient/PatientsHome';
import PatientDashboard from './components/Patient/PatientDashboard';

import Modal from 'react-modal';
import ExamSearch from './components/Exams/ExamSearch';
import RecallHome from './components/Recall/RecallHome';
import AdminHome from './components/Admin/AdminHome';
import WorklistHome from './components/Worklist/WorklistHome';

import Templates from './components/Report/Templates/ReportTemplates';
import Components from './components/Report/Components/ReportComponents';
import Definitions from './components/Report/Definitions/ReportDefinitions';
import Library from './components/Report/Library/ReportLibrary';
import DefinitionHome from './components/Report/Definition/DefinitionHome';
import { userAssociatedFacilities, getAllTypes, getVersion } from './common/services/ApiService';
import { insertNewVersion } from './common/services/CachingService';
import DailyExamDashboard from './components/DailyExams/DailyExamDashboard';
import { activeRoute } from './common/services/SpaService';
import ResetPassword from './components/Auth/ResetPassword';
import DetectActivity from './components/Helpers/DetectActivity';
import Insurance from './components/Maintenance/Insurance';
import CenterDetails from './components/Maintenance/CenterDetails';
import ViewLetterVerification from './components/Admin/View/ViewLetterVerification';

import chalk from 'chalk';
import { Modal as AntModal } from 'antd';
import WhatsNew from './components/WhatsNew/WhatsNew';

import { toast } from '@rickylandino/react-messages';
import ViewAuditHistory from './components/Admin/View/ViewAuditHistory';
import { getLoadInfo } from './common/services/UsersService';
import Dashboard from './components/Dashboard/Dashboard';

// React Query setup for Dashboard
import { QueryClientProvider } from '@tanstack/react-query';
import queryClient from './config/queryClient';

function App(props) {

    const [state, setState] = useState({
        selectedFacility: {},
        theme: window.sessionStorage.getItem("theme") || "light",
        version: '',
        refreshCache: false
    });


    const [refreshCache, setRefreshCache] = useState(false);

    // Globals.theme is a static, in-memory field that resets to its class default ("light")
    // on every page load/refresh, while state.theme (and sessionStorage) correctly persist the
    // saved theme. Without this, any component reading Globals.theme directly (rather than a
    // reactive theme prop/state) renders light-mode colors after a refresh even though the rest
    // of the app correctly shows dark mode - keep it in sync here rather than only inside toggleDarkMode.
    useEffect(() => {
        Globals.theme = state.theme;
    }, [state.theme]);

    // The [data-theme] attribute driving all --bg-color/--text-color/etc CSS custom
    // properties is normally only set on an inner wrapper div below. CSS custom properties
    // only inherit to DOM descendants, and portaled content (antd Select/DatePicker dropdowns,
    // react-bootstrap modals via ReactDOM.createPortal) gets appended directly to document.body
    // as a SIBLING of that wrapper, not a descendant - so it never sees the dark-theme variable
    // values and silently renders with light-mode defaults. Setting the attribute on <html> as
    // well makes it a true ancestor of everything, portaled or not.
    useEffect(() => {
        document.documentElement.setAttribute('data-theme', state.theme);
    }, [state.theme]);

    useEffect(() => {
        if (window.sessionStorage.getItem("userInfo")) {
            var userInfo = JSON.parse(window.sessionStorage.getItem("userInfo"));
            Globals.userInfo = userInfo;
            Globals.tokenStuff = JSON.parse(window.sessionStorage.getItem("tokenStuff"));
            Globals.bearer = Globals.tokenStuff.accessToken;
            Globals.nextGenToken = userInfo.nextGenToken;
            getLoadInfo(Globals.userInfo.userId).then(data => {
                userAssociatedFacilities.next(data.userAssociatedFacilities);
                Globals.associatedFacilities = data.userAssociatedFacilities;
            })
            getAllTypes();
        }

        if (window.sessionStorage.getItem("defaultFacility")) {
            Globals.defaultFacility = JSON.parse(window.sessionStorage.getItem("defaultFacility"));
        }

        Modal.setAppElement('#root');

        getVersionInformation();

        AntModal.destroyAll();
    }, []);

    useEffect(() => {
        activeRoute.next(props.history.location.pathname);
    }, [props.history.location]);

    async function emptyCache(appVersion) {
        window.localStorage.setItem("PINK_APP_VERSION", appVersion);

        if ('caches' in window) {
            // Service worker cache should be cleared with caches.delete()
            const cacheKeys = await window.caches.keys();
            await Promise.all(cacheKeys.map(key => {
                window.caches.delete(key)
            }));
        }

        window.location.replace(window.location.href);
    }

    async function getVersionInformation () {
        let basePath = window.location.href;
        var baseUrl = basePath.replace(/\/+$/, '') + '/' + 'meta.json';

        const response = await fetch(baseUrl, {
            cache: 'no-store'
        });

        const meta = await response.json();

        getVersion().then(data => {
            console.log(data);
            if (`${meta.version}` !== data.cacheVersion) {
                toast.loading("Update in progress");

                setRefreshCache(true);

                insertNewVersion(meta.version).then(data => {
                    window.sessionStorage.setItem("cacheRefreshed", true);
                    emptyCache(meta.version);
                });
            } else if ((window?.localStorage?.PINK_APP_VERSION && (window?.localStorage?.PINK_APP_VERSION !== data.cacheVersion)) || !window?.localStorage?.PINK_APP_VERSION) {
                toast.loading("Update in progress");
                setRefreshCache(true);

                window.sessionStorage.setItem("cacheRefreshed", true);
                emptyCache(data.cacheVersion);
            }

            let versionToUse = data.displayVersion;

            setState({
                ...state,
                version: versionToUse
            });
        });

        console.log(chalk.blueBright("Version: " + window.localStorage?.PINK_APP_VERSION));
    }

    function changeFacility() {
        setState({
            ...state,
            selectedFacility: Globals.selectedFacility
        });
    }

    function updatePatientsLoading() {
        setState({
            ...state,
            loadingPatients: Globals.loadingPatients
        });
    }

    function updatePatients() {
        setState({
            ...state,
            patientsLoaded: true,
            loadingPatients: false
        });
        Globals.loadingPatients = false;
    }

    function toggleDarkMode(theme) {

        setState({
            ...state,
            theme
        });

        Globals.theme = theme;
    }

    const handleRedirectHome = () => {
        return (<Redirect to='/' />)
    }

    return (
        <QueryClientProvider client={queryClient}>
            <div className="nav-function-top" data-theme={state.theme}>
                {
                    (props.location.pathname === '/' || props.location.pathname === '/reset-password') ? (
                        <div>
                            <Route exact path='/' render={(props) => <Login toggleDarkMode={toggleDarkMode} />} />
                            <Route exact path='/reset-password' component={ResetPassword} />
                        </div>
                    ) : window.sessionStorage.getItem("userInfo") ? (

                            <DetectActivity>
                                <Layout changeFacility={changeFacility} updatePatientsLoading={updatePatientsLoading} updatePatients={updatePatients} toggleDarkMode={toggleDarkMode} handleRedirectHome={handleRedirectHome}>
                                <Switch>
                                    <Route path='/patients' render={(props) => <PatientsHome selectedFacility={state.selectedFacility} loadingPatients={state.loadingPatients} patientsLoaded={state.patientsLoaded} />} />
                                    <Route exact path='/patient-dashboard' render={(props) => (props.location.state ? <PatientDashboard updatePatients={updatePatients} /> : <Redirect to='/patients' />)} />
                                    <Route exact path='/admin-panel' render={(props) => (JSON.parse(window.sessionStorage.getItem("userInfo")).isAdmin ? <AdminHome /> : <Redirect to='/patients' />)} />
                                    <Route exact path='/report-definitions' render={(props) => (JSON.parse(window.sessionStorage.getItem("userInfo")).isAdmin ? <DefinitionHome /> : <Redirect to='/patients' />)} />
                                    <Route exact path='/providers' render={(props) => (JSON.parse(window.sessionStorage.getItem("userInfo")).isAdmin ? <Providers /> : <Redirect to='/patients' />)} />
                                    <Route exact path='/audit-history' render={(props) => ((JSON.parse(window.sessionStorage.getItem("userInfo")).isAdmin || JSON.parse(window.sessionStorage.getItem("userInfo")).managerYN) ? <ViewAuditHistory /> : <Redirect to='/patients' />)} />
                                </Switch>

                                <Route path='/recall' component={RecallHome} />
                                <Route path='/insurance-maintenance' component={Insurance} />
                                <Route path='/center-details' component={CenterDetails} />
                                <Route path='/letter-verification' component={ViewLetterVerification} />
                                <Route path='/exam-search' component={ExamSearch} />
                                <Route path='/worklist' render={(props) => <WorklistHome selectedFacility={state.selectedFacility} />} />
                                <Route path='/reporttemplates' component={Templates} />
                                <Route path='/reportcomponents' component={Components} />
                                <Route path='/reportdefinitions' component={Definitions} />
                                <Route path='/reportlibrary' component={Library} />
                                <Route path='/daily-exams' component={DailyExamDashboard} />
                                    <Route path="/what's-new" component={WhatsNew} />
                                    <Route path="/dashboard" component={Dashboard} />
                                </Layout>
                            </DetectActivity>)
                            :
                            <Redirect to='/' />
                }

                {(props.location.pathname === '/' || props.location.pathname === '/reset-password') ? 
                    <div style={{ position: 'fixed', bottom: '0', left: '10px' }}><span className="hidden-md-down fw-500"><i className="far fa-copyright"></i> {new Date().getFullYear()} <span className="fw-700">ONsite Women's Health.</span> All Rights Reserved</span> &nbsp; v{state.version}</div>
                    :
                    <footer className="page-footer" role="contentinfo">
                        <div className="d-flex align-items-center flex-1 text-muted">

                            <span className="hidden-md-down fw-500"><i className="far fa-copyright"></i> {new Date().getFullYear()} <span className="fw-700">ONsite Women's Health.</span> All Rights Reserved</span> &nbsp; v{state.version}
                        </div>
                    </footer>
                }

                {refreshCache && <div className="refresh-overlay" />}
            </div>
        </QueryClientProvider>
    );
}

export default withRouter(App);